/**
 * Role helpers for Planifikuesi (vacation planner).
 *
 * - Employee: read-only view of own leave + month filter
 * - Supervisor: manage leave for reports (employeeSupervisor* match)
 * - Admin / Super Admin / Branch Manager: full company (branch still team-scoped)
 */

import {
  filterEmployeesByTeam,
  getManagedTeam,
  isBranchManager,
} from "../../../../utils/branchScope";

export function normalizePlanerRole(role) {
  return String(role || "")
    .toLowerCase()
    .trim();
}

export function getPlanerUserRole(auth) {
  return normalizePlanerRole(
    auth?.userRole || auth?.user?.userRole || auth?.userAccess?.[0]?.userRole
  );
}

/** Can approve / create / reschedule / delete in Planifikuesi. */
export function canManagePlaner(auth) {
  const role = getPlanerUserRole(auth);
  return (
    role === "admin" ||
    role === "super admin" ||
    role === "supervisor" ||
    role === "branch manager"
  );
}

export function isPlanerEmployeeOnly(auth) {
  return !canManagePlaner(auth);
}

function supervisorDisplayName(employee) {
  if (!employee) return "";
  return `${employee.employeeFirstName || ""} ${employee.employeeLastName || ""}`
    .trim()
    .toLowerCase();
}

/**
 * Employees a Supervisor may manage in Planifikuesi.
 * Matches employeeSupervisorId → supervisor.employeeId, or
 * employeeSupervisor name → supervisor full name.
 * Always includes the supervisor themselves.
 */
export function getSupervisedEmployees(employees, auth) {
  const me = auth?.employee;
  const list = Array.isArray(employees) ? employees : [];
  if (!me) return [];

  const myId = me.employeeId;
  const myName = supervisorDisplayName(me);

  return list.filter((e) => {
    if (myId && e?.employeeId === myId) return true;
    if (myId && e?.employeeSupervisorId && e.employeeSupervisorId === myId) {
      return true;
    }
    const sup = String(e?.employeeSupervisor || "")
      .trim()
      .toLowerCase();
    return !!sup && !!myName && sup === myName;
  });
}

/**
 * Roster visible in Planifikuesi for the current viewer.
 */
export function scopePlanerEmployees(employees, auth) {
  const list = Array.isArray(employees) ? employees : [];
  const role = getPlanerUserRole(auth);

  if (role === "admin" || role === "super admin") {
    return list;
  }

  if (role === "supervisor") {
    return getSupervisedEmployees(list, auth);
  }

  if (isBranchManager(auth)) {
    return filterEmployeesByTeam(list, getManagedTeam(auth));
  }

  // Employee (and any other non-manager role): self only.
  const myId = auth?.employeeId || auth?.employee?.employeeId;
  const mySub = auth?.user?.userSub || auth?.employee?.userSub;
  return list.filter(
    (e) =>
      (myId && e?.employeeId === myId) || (mySub && e?.userSub === mySub)
  );
}
